import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ClockIcon,
  PencilSquareIcon,
  UserPlusIcon,
  TrashIcon,
  DocumentTextIcon,
  SparklesIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { Report, CreateReportRequest, UpdateReportRequest, User } from '../types/index';
import apiService from '../services/api';
import CaseTimeline from './CaseTimeline';
import PageHeader from './ui/PageHeader';
import Badge from './ui/Badge';
import Button from './ui/Button';
import Modal from './ui/Modal';
import EmptyState from './ui/EmptyState';

const STATUS_OPTIONS = ['pending', 'assigned', 'in_progress', 'resolved', 'closed'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];
const PAGE_SIZE = 15;

const emptyForm: CreateReportRequest = {
  title: '',
  description: '',
  categoryId: '',
  priority: 'medium',
  latitude: 0,
  longitude: 0,
  address: '',
  photoUrls: [],
};

const Reports: React.FC = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [technicians, setTechnicians] = useState<User[]>([]);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<string>('');
  const [isLoadingTechnicians, setIsLoadingTechnicians] = useState(false);
  const [formData, setFormData] = useState<CreateReportRequest>(emptyForm);

  // Table controls
  const [search, setSearch] = useState('');
  // Dashboard/notification-bell tiles deep-link here with ?status=… or
  // ?priority=…, so the filter dropdowns need to pick that up on load —
  // otherwise clicking "12 in progress" on the dashboard just dumps you on
  // an unfiltered list and you have to re-select it yourself.
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || 'all');
  const [page, setPage] = useState(1);

  // AI-flagged possible duplicates awaiting staff review (see role-portals.js
  // — created when a citizen overrides the semantic duplicate warning).
  interface PossibleDuplicate {
    linkId: string;
    similarityScore: string | number;
    flaggedAt: string;
    masterId: string;
    masterTitle: string;
    masterStatus: string;
    linkedId: string;
    linkedTitle: string;
    linkedStatus: string;
    linkedDescription: string;
  }
  const [possibleDuplicates, setPossibleDuplicates] = useState<PossibleDuplicate[]>([]);
  const [resolvingLinkId, setResolvingLinkId] = useState<string | null>(null);

  // Semantic search: finds reports by meaning, not just exact substring —
  // reuses the same embeddings stored for duplicate detection. "AI search"
  // is a distinct, opt-in mode from the plain text filter below; clearing it
  // returns to normal browsing.
  const [aiQuery, setAiQuery] = useState('');
  const [aiResults, setAiResults] = useState<Array<{ id: string; title: string; similarity: number }>>([]);
  const [aiSearching, setAiSearching] = useState(false);
  const [aiSearchActive, setAiSearchActive] = useState(false);
  const [aiSearchError, setAiSearchError] = useState('');

  useEffect(() => {
    fetchReports();
    fetchPossibleDuplicates();
    const interval = setInterval(() => fetchReports(), 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchReports = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getReports();
      if (response.success && response.data) {
        setReports(response.data);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPossibleDuplicates = async () => {
    try {
      const response = await apiService.get<PossibleDuplicate[]>('/platform/reports/possible-duplicates');
      if (response.success && response.data) setPossibleDuplicates(response.data);
    } catch (error) {
      console.error('Error fetching possible duplicates:', error);
    }
  };

  const runAiSearch = async () => {
    if (!aiQuery.trim()) return;
    setAiSearching(true);
    setAiSearchError('');
    try {
      const response = await apiService.get<Array<{ id: string; title: string; similarity: number }>>(
        `/platform/reports/semantic-search?q=${encodeURIComponent(aiQuery.trim())}`
      );
      if (response.success && response.data) {
        setAiResults(response.data);
        setAiSearchActive(true);
        if (response.data.length === 0) setAiSearchError('No similar reports found.');
      } else {
        setAiSearchError('AI search is unavailable right now.');
      }
    } catch (error) {
      console.error('Error running AI search:', error);
      setAiSearchError('AI search is unavailable right now.');
    } finally {
      setAiSearching(false);
    }
  };

  const clearAiSearch = () => {
    setAiSearchActive(false);
    setAiResults([]);
    setAiQuery('');
    setAiSearchError('');
  };

  const resolvePossibleDuplicate = async (linkId: string, action: 'confirm' | 'dismiss') => {
    setResolvingLinkId(linkId);
    try {
      await apiService.post(`/platform/reports/possible-duplicates/${linkId}/resolve`, { action });
      setPossibleDuplicates((prev) => prev.filter((d) => d.linkId !== linkId));
      if (action === 'confirm') fetchReports();
    } catch (error) {
      console.error('Error resolving possible duplicate:', error);
    } finally {
      setResolvingLinkId(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await apiService.createReport(formData);
      if (response.success && response.data) {
        setReports((prev) => [...prev, response.data!]);
        setShowCreateModal(false);
        setFormData(emptyForm);
      }
    } catch (error) {
      console.error('Error creating report:', error);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReport) return;
    try {
      const updateData: UpdateReportRequest = {
        title: formData.title,
        description: formData.description,
        categoryId: formData.categoryId,
        priority: formData.priority as any,
      };
      const response = await apiService.updateReport(selectedReport.id, updateData);
      if (response.success && response.data) {
        setReports((prev) => prev.map((report) => (report.id === selectedReport.id ? response.data! : report)));
        setShowEditModal(false);
        setSelectedReport(null);
        setFormData(emptyForm);
      }
    } catch (error) {
      console.error('Error updating report:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    try {
      const response = await apiService.deleteReport(id);
      if (response.success) {
        setReports((prev) => prev.filter((report) => report.id !== id));
      }
    } catch (error) {
      console.error('Error deleting report:', error);
    }
  };

  const openEditModal = (report: Report) => {
    setSelectedReport(report);
    setFormData({
      title: report.title,
      description: report.description,
      categoryId: report.categoryId || '',
      priority: report.priority,
      latitude: parseFloat(report.latitude || '0'),
      longitude: parseFloat(report.longitude || '0'),
      address: report.address || '',
      photoUrls: report.photoUrls || [],
    });
    setShowEditModal(true);
  };

  const openCreateModal = () => {
    setFormData(emptyForm);
    setShowCreateModal(true);
  };

  const openReassignModal = async (report: Report) => {
    setSelectedReport(report);
    setSelectedTechnicianId(report.assignedTechnicianId || '');
    setShowReassignModal(true);
    setIsLoadingTechnicians(true);
    try {
      const response = await apiService.getUsers();
      if (response.success && response.data) {
        // Real technician accounts carry role 'technician' or
        // 'field_worker' — 'municipal_staff' predates those and isn't
        // what field staff are actually created with (see
        // DispatchService.resolveEligibleTechnicians), so this list was
        // always empty of every real technician regardless of who was online.
        setTechnicians(
          response.data.filter(
            (u) => ['municipal_staff', 'technician', 'field_worker'].includes(u.role) && u.isActive
          )
        );
      }
    } catch (error) {
      console.error('Error fetching technicians:', error);
    } finally {
      setIsLoadingTechnicians(false);
    }
  };

  const handleReassign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReport || !selectedTechnicianId || !user) return;
    try {
      await apiService.createAssignment({
        reportId: selectedReport.id,
        assignedToId: selectedTechnicianId,
        assignedById: user.id,
        assignedDepartmentId: selectedReport.departmentId,
        priority: selectedReport.priority,
      });
      try {
        await apiService.updateReportStatus(selectedReport.id, 'assigned');
      } catch (updateError) {
        console.error('Error updating report status:', updateError);
      }
      await fetchReports();
      setShowReassignModal(false);
      setSelectedReport(null);
      setSelectedTechnicianId('');
    } catch (error) {
      console.error('Error reassigning report:', error);
      alert('Failed to reassign report. Please try again.');
    }
  };

  const filteredReports = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reports.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && r.priority !== priorityFilter) return false;
      if (!q) return true;
      const haystack = `${r.title} ${r.reporterFirstName || ''} ${r.reporterLastName || ''} ${r.categoryName || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [reports, search, statusFilter, priorityFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredReports.length / PAGE_SIZE));
  const pageReports = filteredReports.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, priorityFilter]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Reports"
        subtitle={`${filteredReports.length} of ${reports.length} reports · manage and track issue reports from citizens`}
        actions={
          <>
            <Button variant="secondary" icon={ArrowPathIcon} onClick={fetchReports} disabled={isLoading}>
              Refresh
            </Button>
            <Button variant="primary" icon={PlusIcon} onClick={openCreateModal}>
              Create Report
            </Button>
          </>
        }
      />

      {/* AI-flagged possible duplicates — advisory only, staff confirm or dismiss */}
      {possibleDuplicates.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-amber-600" />
            <h3 className="text-sm font-semibold text-amber-900">
              Possible duplicates flagged by AI ({possibleDuplicates.length})
            </h3>
          </div>
          <p className="mb-3 text-xs text-amber-800">
            The citizen was warned this looked similar to an existing report and chose to submit anyway. Review and confirm or dismiss.
          </p>
          <div className="space-y-2">
            {possibleDuplicates.map((d) => (
              <div key={d.linkId} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-white p-3">
                <div className="min-w-[240px] flex-1 text-sm">
                  <div className="font-medium text-gray-900">{d.linkedTitle}</div>
                  <div className="text-xs text-gray-500">
                    similar to <span className="font-medium text-gray-700">"{d.masterTitle}"</span>
                    {' · '}{Math.round(Number(d.similarityScore) * 100)}% match
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    disabled={resolvingLinkId === d.linkId}
                    onClick={() => resolvePossibleDuplicate(d.linkId, 'dismiss')}
                  >
                    Not a duplicate
                  </Button>
                  <Button
                    variant="primary"
                    disabled={resolvingLinkId === d.linkId}
                    onClick={() => resolvePossibleDuplicate(d.linkId, 'confirm')}
                  >
                    Confirm duplicate
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI search — finds reports by meaning, distinct from the plain text
          filter in the Filters bar below. Opt-in, never replaces normal browsing. */}
      <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <SparklesIcon className="h-5 w-5 shrink-0 text-accent-600" />
          <div className="relative flex-1 min-w-[240px]">
            <input
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runAiSearch()}
              placeholder='Ask AI to find reports, e.g. "water pressure problems near the school"'
              className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <Button variant="secondary" disabled={aiSearching || !aiQuery.trim()} onClick={runAiSearch}>
            {aiSearching ? 'Searching…' : 'AI search'}
          </Button>
          {aiSearchActive && (
            <Button variant="ghost" onClick={clearAiSearch}>
              Clear
            </Button>
          )}
        </div>
        {aiSearchError && <p className="mt-2 text-xs text-red-600">{aiSearchError}</p>}
        {aiSearchActive && aiResults.length > 0 && (
          <div className="mt-3 space-y-2">
            {aiResults.map((r) => {
              const fullReport = reports.find((rep) => rep.id === r.id);
              return (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="min-w-[240px] flex-1 text-sm">
                    <div className="font-medium text-gray-900">{r.title}</div>
                    <div className="text-xs text-gray-500">
                      {Math.round(r.similarity * 100)}% match
                      {fullReport && <> · <span className="capitalize">{fullReport.status?.replace('_', ' ')}</span></>}
                    </div>
                  </div>
                  {fullReport && (
                    <Button size="sm" variant="secondary" onClick={() => openEditModal(fullReport)}>
                      View
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, reporter, or category…"
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            const value = e.target.value;
            setStatusFilter(value);
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              if (value === 'all') next.delete('status');
              else next.set('status', value);
              return next;
            });
          }}
          className="rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
            </option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => {
            const value = e.target.value;
            setPriorityFilter(value);
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              if (value === 'all') next.delete('priority');
              else next.set('priority', value);
              return next;
            });
          }}
          className="rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="all">All priorities</option>
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {filteredReports.length === 0 ? (
        <EmptyState
          icon={DocumentTextIcon}
          title={reports.length === 0 ? 'No reports yet' : 'No reports match your filters'}
          description={reports.length === 0 ? 'New citizen reports will appear here.' : 'Try adjusting your search or filters.'}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Report</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Assigned to</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white">
                {pageReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 max-w-xs">
                      <div className="flex items-center gap-1.5">
                        <Link to={`/reports/${report.id}`} className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-primary-700">
                          {report.title}
                        </Link>
                        {(report as any).files?.length > 0 && (
                          <span
                            title={`${(report as any).files.length} attachment${(report as any).files.length === 1 ? '' : 's'}`}
                            className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                          >
                            <PaperClipIcon className="h-3 w-3" />
                            {(report as any).files.length}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                        {report.categoryName || 'Uncategorized'} · {report.reporterFirstName} {report.reporterLastName}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge value={report.status} />
                    </td>
                    <td className="px-4 py-3">
                      <Badge value={report.priority} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{report.assignedTechnicianName || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{new Date(report.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          title="History"
                          icon={ClockIcon}
                          onClick={() => {
                            setSelectedReport(report);
                            setShowTimelineModal(true);
                          }}
                        />
                        <Button size="sm" variant="ghost" title="Edit" icon={PencilSquareIcon} onClick={() => openEditModal(report)} />
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Reassign"
                          icon={UserPlusIcon}
                          onClick={() => openReassignModal(report)}
                        />
                        <Button
                          size="sm"
                          variant="danger-ghost"
                          title="Delete"
                          icon={TrashIcon}
                          onClick={() => handleDelete(report.id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {showTimelineModal && selectedReport && (
        <CaseTimeline
          reportId={selectedReport.id}
          reportNumber={selectedReport.reportNumber}
          onClose={() => {
            setShowTimelineModal(false);
            setSelectedReport(null);
          }}
        />
      )}

      {/* Create Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Report"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button type="submit" form="create-report-form" variant="primary">
              Create
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreate} id="create-report-form" className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
            <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="input-field mt-1" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} className="input-field mt-1" rows={3} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category ID</label>
            <input type="text" name="categoryId" value={formData.categoryId} onChange={handleInputChange} className="input-field mt-1" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Priority</label>
            <select name="priority" value={formData.priority} onChange={handleInputChange} className="input-field mt-1">
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </form>
      </Modal>

      {/* Reassign Modal */}
      <Modal
        open={showReassignModal && !!selectedReport}
        onClose={() => {
          setShowReassignModal(false);
          setSelectedReport(null);
          setSelectedTechnicianId('');
        }}
        title="Reassign Report"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowReassignModal(false);
                setSelectedReport(null);
                setSelectedTechnicianId('');
              }}
            >
              Cancel
            </Button>
            <Button type="submit" form="reassign-report-form" variant="primary" disabled={!selectedTechnicianId || isLoadingTechnicians}>
              Reassign
            </Button>
          </>
        }
      >
        {selectedReport && (
          <>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              Report: <strong>{selectedReport.title}</strong>
            </p>
            {selectedReport.assignedTechnicianName && (
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Currently assigned to: <strong>{selectedReport.assignedTechnicianName}</strong>
              </p>
            )}
            <form onSubmit={handleReassign} id="reassign-report-form" className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Select Technician</label>
                {isLoadingTechnicians ? (
                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">Loading technicians…</div>
                ) : (
                  <select value={selectedTechnicianId} onChange={(e) => setSelectedTechnicianId(e.target.value)} className="input-field mt-1" required>
                    <option value="">-- Select Technician --</option>
                    {technicians.map((tech) => (
                      <option key={tech.id} value={tech.id}>
                        {tech.firstName} {tech.lastName} ({tech.email})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </form>
          </>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={showEditModal && !!selectedReport}
        onClose={() => setShowEditModal(false)}
        title="Edit Report"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button type="submit" form="edit-report-form" variant="primary">
              Update
            </Button>
          </>
        }
      >
        <form onSubmit={handleEdit} id="edit-report-form" className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
            <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="input-field mt-1" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} className="input-field mt-1" rows={3} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category ID</label>
            <input type="text" name="categoryId" value={formData.categoryId} onChange={handleInputChange} className="input-field mt-1" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Priority</label>
            <select name="priority" value={formData.priority} onChange={handleInputChange} className="input-field mt-1">
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Reports;
